/**
 * Renderer setup and the 2D camera.
 *
 * WebGPU is used when the browser offers it and WebGL2 is the fallback. Both
 * paths run the exact same TSL node materials, so there is no second shader
 * implementation to keep in sync.
 */

import * as THREE from 'three/webgpu';
import { buildParticleTexture, buildTerrainAtlas, buildTurretAtlas, buildUnitAtlas } from './art.js';
import { TerrainLayer } from './terrain.js';
import { EntityLayer } from './entities.js';
import { EffectsLayer } from './effects.js';

export interface CameraState {
  /** Centre of the view, in world tiles. */
  x: number;
  y: number;
  /** Tiles visible across the viewport height. */
  zoom: number;
}

const MIN_ZOOM = 12;
const MAX_ZOOM = 90;

export class GameRenderer {
  readonly renderer: THREE.WebGPURenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.OrthographicCamera;
  readonly terrain: TerrainLayer;
  readonly entities: EntityLayer;
  readonly effects: EffectsLayer;

  readonly cam: CameraState = { x: 32, y: 32, zoom: 34 };
  private mapWidth = 128;
  private mapHeight = 128;

  /** Which API actually initialised, for the diagnostics readout. */
  backend = 'unknown';

  private constructor(canvas: HTMLCanvasElement, renderer: THREE.WebGPURenderer) {
    this.renderer = renderer;
    this.scene.background = new THREE.Color('#0d1117');

    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 100);
    this.camera.position.set(0, 0, 10);

    const terrainAtlas = buildTerrainAtlas();
    const unitAtlas = buildUnitAtlas();
    const turretAtlas = buildTurretAtlas();
    const particleTex = buildParticleTexture();

    this.terrain = new TerrainLayer(terrainAtlas);
    this.entities = new EntityLayer(unitAtlas, turretAtlas);
    this.effects = new EffectsLayer(particleTex);

    this.scene.add(this.terrain.mesh);
    this.entities.addTo(this.scene);
    this.effects.addTo(this.scene);

    void canvas;
    this.resize();
  }

  /**
   * Creates the renderer, preferring WebGPU.
   *
   * `renderer.init()` is what actually acquires the GPU device, so it has to be
   * awaited before anything is drawn. If WebGPU is unavailable three falls back
   * to WebGL2 on its own; if that fails too, the caller gets a rejected promise
   * and shows a plain error instead of a blank canvas.
   */
  static async create(canvas: HTMLCanvasElement): Promise<GameRenderer> {
    const hasWebGPU = typeof navigator !== 'undefined' && 'gpu' in navigator;

    const renderer = new THREE.WebGPURenderer({
      canvas,
      antialias: true,
      alpha: false,
      forceWebGL: !hasWebGPU,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    await renderer.init();

    const game = new GameRenderer(canvas, renderer);
    // `isWebGPUBackend` is set by the backend implementation but is not part of
    // the published `Backend` type, so it has to be read through a cast.
    const backend = renderer.backend as unknown as { isWebGPUBackend?: boolean };
    game.backend = backend?.isWebGPUBackend ? 'WebGPU' : 'WebGL2';
    return game;
  }

  setMap(width: number, height: number, tiles: Uint8Array): void {
    this.mapWidth = width;
    this.mapHeight = height;
    this.terrain.setMap(width, height, tiles);
    this.cam.x = width / 2;
    this.cam.y = height / 2;
  }

  /** Recomputes the orthographic frustum for the current canvas and zoom. */
  resize(): void {
    const canvas = this.renderer.domElement as HTMLCanvasElement;
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);

    const aspect = w / Math.max(1, h);
    const halfH = this.cam.zoom / 2;
    const halfW = halfH * aspect;
    this.camera.left = -halfW;
    this.camera.right = halfW;
    this.camera.top = halfH;
    this.camera.bottom = -halfH;
    this.camera.updateProjectionMatrix();
  }

  /** Keeps the view over the map and refreshes the projection. */
  applyCamera(): void {
    const canvas = this.renderer.domElement as HTMLCanvasElement;
    const aspect = (canvas.clientWidth || 1) / Math.max(1, canvas.clientHeight);
    const halfH = this.cam.zoom / 2;
    const halfW = halfH * aspect;

    // Clamp so the player cannot pan the map entirely off screen.
    this.cam.x = Math.min(Math.max(this.cam.x, -halfW * 0.4), this.mapWidth + halfW * 0.4);
    this.cam.y = Math.min(Math.max(this.cam.y, -halfH * 0.4), this.mapHeight + halfH * 0.4);

    this.camera.left = -halfW;
    this.camera.right = halfW;
    this.camera.top = halfH;
    this.camera.bottom = -halfH;
    this.camera.position.x = this.cam.x;
    // World +y points down the screen, so the camera y is negated throughout.
    this.camera.position.y = -this.cam.y;
    this.camera.updateProjectionMatrix();
  }

  zoomBy(factor: number, anchor?: { x: number; y: number }): void {
    const before = anchor ? this.screenToWorld(anchor.x, anchor.y) : null;
    this.cam.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.cam.zoom * factor));
    this.applyCamera();
    if (before && anchor) {
      // Keep the world point under the cursor pinned while zooming.
      const after = this.screenToWorld(anchor.x, anchor.y);
      this.cam.x += before.x - after.x;
      this.cam.y += before.y - after.y;
      this.applyCamera();
    }
  }

  /** Converts a CSS pixel position on the canvas into world tile coordinates. */
  screenToWorld(px: number, py: number): { x: number; y: number } {
    const canvas = this.renderer.domElement as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const nx = ((px - rect.left) / rect.width) * 2 - 1;
    const ny = -(((py - rect.top) / rect.height) * 2 - 1);
    const halfH = this.cam.zoom / 2;
    const halfW = halfH * (rect.width / Math.max(1, rect.height));
    return {
      x: this.cam.x + nx * halfW,
      y: this.cam.y - ny * halfH,
    };
  }

  /** Converts world tile coordinates back into CSS pixels on the canvas. */
  worldToScreen(wx: number, wy: number): { x: number; y: number } {
    const canvas = this.renderer.domElement as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const halfH = this.cam.zoom / 2;
    const halfW = halfH * (rect.width / Math.max(1, rect.height));
    const nx = (wx - this.cam.x) / halfW;
    const ny = -(wy - this.cam.y) / halfH;
    return {
      x: ((nx + 1) / 2) * rect.width + rect.left,
      y: ((1 - ny) / 2) * rect.height + rect.top,
    };
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.terrain.dispose();
    this.entities.dispose();
    this.effects.dispose();
    this.renderer.dispose();
  }
}
